/**
 * Las reglas del inbox, fuera de los componentes para poder probarlas sin DOM —
 * el mismo reparto que `screens/agency/lib/format.ts`.
 *
 * Lo que vive acá y no en una pantalla: qué entra en cada filtro, en qué orden,
 * cómo se escribe un timestamp relativo, y qué frase le corresponde a cada
 * `code` de error de la respuesta. Son decisiones de producto, no de layout.
 */
import type { ToneName } from '~/components/ui';
import type { CandidateInvitation, ThreadSummary } from '../api/threadsClient';
import { ThreadApiError } from '../api/threadsClient';
import { formatDate, formatDateTime, getLocale, messages, type Locale } from '~/i18n';

/** El único estado en que la invitación le toca contestar al candidato. */
export const PENDING_INVITATION = 'contact_requested';

/**
 * Evento que dispara la pantalla de hilo cuando abrirlo dejó mensajes leídos.
 * El nav global es de otro dueño (`JobApplyLayout`), así que en vez de importar
 * su estado, el inbox avisa y el nav decide si vuelve a pedir `/threads/meta`.
 */
export const INBOX_UPDATED_EVENT = 'tbit:inbox-updated';

export function notifyInboxChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent(INBOX_UPDATED_EVENT));
  } catch {
    /* Un entorno sin window (un render de prueba) no tiene nav que avisar. */
  }
}

/**
 * La etiqueta del estado de un hilo en el idioma activo; un estado que no
 * conocemos se muestra tal cual. Función y no constante: una constante de
 * módulo congelaría el idioma con el que cargó la app.
 */
export function threadStatusLabel(status: string, locale: Locale = getLocale()): string {
  return messages(locale).workspace.thread.status[status] ?? status;
}

/** Mismo semáforo que el panel y el historial: mint lo que contestó el otro
 *  lado, coral la fricción, púrpura lo que está en marcha. */
export const THREAD_STATUS_TONES: Record<string, ToneName> = {
  SENT: 'info',
  REPLIED: 'good',
  INTERVIEW: 'good',
  REJECTED: 'bad',
  NO_RESPONSE: 'warn',
};

export type InboxFilter = 'all' | 'unread' | 'invitations';

export type InboxItem =
  | { kind: 'thread'; id: string; at: string; thread: ThreadSummary }
  | { kind: 'invitation'; id: string; at: string; invitation: CandidateInvitation };

export function isPendingInvitation(invitation: CandidateInvitation): boolean {
  return invitation.status === PENDING_INVITATION;
}

function threadItem(thread: ThreadSummary): InboxItem {
  return { kind: 'thread', id: `t:${thread._id}`, at: thread.last_message_at, thread };
}

function invitationItem(invitation: CandidateInvitation): InboxItem {
  // Una invitación contestada se ordena por la respuesta; una pendiente, por el
  // día en que llegó. Es la fecha de "lo último que pasó", igual que en un hilo.
  return {
    kind: 'invitation',
    id: `i:${invitation._id}`,
    at: invitation.responded_at ?? invitation.invited_at ?? '',
    invitation,
  };
}

function millis(value: string): number {
  const time = new Date(value).getTime();
  // Una fecha ilegible se va al fondo en vez de mandar la fila al tope, que es
  // lo que hace NaN cuando el comparador lo deja pasar.
  return Number.isNaN(time) ? 0 : time;
}

function byRecency(a: InboxItem, b: InboxItem): number {
  return millis(b.at) - millis(a.at);
}

/**
 * La lista que ve el candidato.
 *
 * 'unread' es "lo que espera por mí", no literalmente "mensajes sin abrir": son
 * los hilos con respuestas nuevas MÁS las invitaciones sin contestar. Está
 * hecho a propósito para que coincida con el badge del nav, que es
 * `total_unread + pending_invitations` de `/threads/meta`: un badge que cuenta
 * una cosa y un filtro que muestra otra es un badge que no se puede vaciar.
 */
export function buildInbox(
  threads: ThreadSummary[],
  invitations: CandidateInvitation[],
  filter: InboxFilter,
): InboxItem[] {
  const items: InboxItem[] = [];

  if (filter === 'invitations') {
    // Acá sí entran las contestadas: el filtro es también el historial.
    items.push(...invitations.map(invitationItem));
  } else if (filter === 'unread') {
    items.push(...threads.filter((thread) => thread.unread_count > 0).map(threadItem));
    items.push(...invitations.filter(isPendingInvitation).map(invitationItem));
  } else {
    items.push(...threads.map(threadItem));
    items.push(...invitations.filter(isPendingInvitation).map(invitationItem));
  }

  return items.sort(byRecency);
}

/** Cada contador es exactamente lo que ese filtro va a mostrar. */
export function inboxCounts(
  threads: ThreadSummary[],
  invitations: CandidateInvitation[],
): Record<InboxFilter, number> {
  return {
    all: buildInbox(threads, invitations, 'all').length,
    unread: buildInbox(threads, invitations, 'unread').length,
    invitations: invitations.length,
  };
}

/**
 * El timestamp de un inbox: relativo mientras es reciente, fecha cuando ya no.
 *
 * Nunca imprime futuro. El `created_at` lo pone el servidor y el reloj del
 * navegador puede ir atrasado unos segundos, y "en 2 min" sobre un correo que
 * ya llegó lee como un error de la app.
 */
export function relativeTime(
  value: string | Date | null | undefined,
  now: Date = new Date(),
  locale: Locale = getLocale(),
): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const t = messages(locale).workspace.time;
  const elapsed = Math.max(0, now.getTime() - date.getTime());
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return t.now;
  if (minutes < 60) return t.minutes(minutes);

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t.hours(hours);

  const days = Math.floor(hours / 24);
  if (days === 1) return t.yesterday;
  if (days < 7) return t.days(days);

  const sameYear = date.getFullYear() === now.getFullYear();
  return formatDate(
    date,
    sameYear
      ? { day: 'numeric', month: 'short' }
      : { day: 'numeric', month: 'short', year: 'numeric' },
    locale,
  );
}

/** La fecha completa que va en el `title` de un timestamp relativo. */
export function absoluteTime(
  value: string | Date | null | undefined,
  locale: Locale = getLocale(),
): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatDateTime(
    date,
    {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    },
    locale,
  );
}

/**
 * A dónde manda el botón "Abrir en Gmail".
 *
 * Con el id del hilo es un enlace directo. Sin él —una postulación vieja, o un
 * payload que todavía no trae el campo— lo único honesto que queda es dejar a
 * Gmail buscando ese asunto: mandar a la bandeja pelada obligaría al candidato a
 * encontrar el correo a mano, que es justo lo que el botón promete evitar.
 */
export function gmailThreadUrl(thread: { gmail_thread_id?: string | null; subject?: string }): string {
  const id = (thread.gmail_thread_id ?? '').trim();
  if (id) return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(id)}`;

  const subject = (thread.subject ?? '').trim();
  if (subject) {
    return `https://mail.google.com/mail/u/0/#search/${encodeURIComponent(`subject:"${subject}"`)}`;
  }
  return 'https://mail.google.com/mail/u/0/#all';
}

/**
 * La frase que ve el candidato cuando una respuesta no salió.
 *
 * Cada `code` del backend tiene la suya porque significan cosas distintas para
 * quien está escribiendo: el 502 quiere decir "tu texto sigue acá, vuelve a
 * intentar" y el 409 de gmail quiere decir "esto no se responde desde acá".
 */
export function replyErrorMessage(error: unknown, locale: Locale = getLocale()): string {
  const t = messages(locale).workspace.replyErrors;
  if (!(error instanceof ThreadApiError)) {
    return t.network;
  }

  switch (error.code) {
    case 'gmail_channel':
      return t.gmailChannel;
    case 'rate_limited': {
      const minutes = Math.max(1, Math.ceil((error.retryAfterSeconds ?? 3600) / 60));
      return t.rateLimited(minutes);
    }
    case 'bounced':
      return t.bounced;
    case 'send_failed':
      return t.sendFailed;
    case 'thread_not_sent':
      return t.threadNotSent;
    case 'no_contact_email':
      return t.noContactEmail;
    case 'sender_not_provisioned':
      return t.senderNotProvisioned;
    case 'gmail_reconnect_required':
      return t.gmailReconnect;
    case 'gmail_unavailable':
      return t.gmailUnavailable;
    case 'gmail_thread_missing':
      return t.gmailThreadMissing;
    case 'recipient_changed':
      return t.recipientChanged;
    case 'reply_in_progress':
      return t.replyInProgress;
    default:
      if (error.status === 404) return t.notFound;
      return t.generic;
  }
}
